// Service read-only para dados da MF na Bianca CRM + portfólio mf_imoveis.
import { supabase } from '../../../../services/supabaseClient';

export const MF_TOKEN = 'QOQMRWtDQA9W8pluLNSV';

// Carrega só os produtos (campanhas) da MF — pra usar no seletor do form
export async function fetchBiancaProdutos() {
  const { data } = await supabase
    .from('bianca_produtos')
    .select('product_id, Nome, ativo')
    .eq('token', MF_TOKEN)
    .order('Nome');
  return data || [];
}

export async function fetchBiancaData() {
  const [{ data: produtos }, { data: leads }, { data: cliente }, { data: imoveis }] = await Promise.all([
    supabase
      .from('bianca_produtos')
      .select('product_id, Nome, ativo')
      .eq('token', MF_TOKEN),
    supabase
      .from('bianca_leads')
      .select(
        'lead_id, nome, telefone, status, lista_status, qualificacao, ' +
        'valor_venda, valor_contrato, comissao, ' +
        'created_at, updated_at, ultima_mensagem, ultima_mensagem_recebida, ' +
        'data_e_hora_agendamento, agendamento_created_at, ' +
        'proximo_fup, status_fup, qtd_fup, ' +
        'product_id, produto, origem, campanha, observacoes'
      )
      .eq('token', MF_TOKEN)
      .order('created_at', { ascending: false }),
    supabase
      .from('BIANCA_CLIENTES')
      .select('nome_da_empresa, leads_hoje, ordem_kanban')
      .eq('token', MF_TOKEN)
      .single(),
    supabase
      .from('mf_imoveis')
      .select('id, titulo, bairro, cidade, area_m2, preco, status, destaque, imagens, condominio, bianca_product_id, created_at')
      .order('created_at', { ascending: false }),
  ]);

  return {
    produtos: produtos || [],
    leads: leads || [],
    cliente: cliente || null,
    imoveis: imoveis || [],
  };
}

export function computeMetrics({ leads, produtos, imoveis }, period = 30) {
  const now = Date.now();
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const startOfPrevMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const periodStart = new Date(today); periodStart.setDate(periodStart.getDate() - (period - 1));
  const next7days = new Date(today); next7days.setDate(next7days.getDate() + 7);

  // ===== KPIs =====
  const leadsNoMes = leads.filter(l => l.created_at && new Date(l.created_at) >= startOfMonth).length;
  const leadsMesAnterior = leads.filter(l => {
    if (!l.created_at) return false;
    const d = new Date(l.created_at);
    return d >= startOfPrevMonth && d < startOfMonth;
  }).length;
  const trendPct = leadsMesAnterior > 0
    ? Math.round(((leadsNoMes - leadsMesAnterior) / leadsMesAnterior) * 100)
    : null;

  // Conversão lead → visita (agendados / total leads do mês)
  const agendadosNoMes = leads.filter(l => {
    if (!l.created_at || new Date(l.created_at) < startOfMonth) return false;
    return l.status === 'agendado' || l.data_e_hora_agendamento;
  }).length;
  const conversaoMes = leadsNoMes > 0 ? Math.round((agendadosNoMes / leadsNoMes) * 100) : 0;
  // Mês anterior pra trend
  const agendadosMesAnt = leads.filter(l => {
    if (!l.created_at) return false;
    const d = new Date(l.created_at);
    if (d < startOfPrevMonth || d >= startOfMonth) return false;
    return l.status === 'agendado' || l.data_e_hora_agendamento;
  }).length;
  const conversaoMesAnt = leadsMesAnterior > 0 ? Math.round((agendadosMesAnt / leadsMesAnterior) * 100) : 0;
  const conversaoTrend = conversaoMes - conversaoMesAnt;

  // Visitas próximas 7 dias
  const visitas = leads
    .map(l => ({ ...l, _agData: parseAgendamento(l.data_e_hora_agendamento) }))
    .filter(l => l._agData && l._agData >= today && l._agData <= next7days);
  const visitasProx7 = visitas.length;
  const visitasOrdenadas = [...visitas].sort((a, b) => a._agData - b._agData);
  const visitasHoje = visitasOrdenadas.filter(v => sameDay(v._agData, today)).length;
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const visitasAmanha = visitasOrdenadas.filter(v => sameDay(v._agData, tomorrow)).length;

  // Ticket médio do portfólio publicado
  const precos = (imoveis || []).filter(i => i.status === 'publicado' && i.preco).map(i => Number(i.preco));
  const ticketMedio = precos.length ? precos.reduce((s, p) => s + p, 0) / precos.length : 0;

  // ===== Chart série =====
  const porDia = {};
  for (let i = period - 1; i >= 0; i--) {
    const d = new Date(today); d.setDate(d.getDate() - i);
    porDia[d.toISOString().slice(0, 10)] = 0;
  }
  for (const l of leads) {
    if (!l.created_at) continue;
    const k = String(l.created_at).slice(0, 10);
    if (k in porDia) porDia[k]++;
  }
  const serieDias = Object.entries(porDia).map(([data, n]) => ({ data, n }));
  const totalPeriodo = serieDias.reduce((s, d) => s + d.n, 0);
  const mediaDia = serieDias.length ? (totalPeriodo / serieDias.length).toFixed(1) : '0';
  const pico = serieDias.reduce((m, d) => d.n > m.n ? d : m, { n: 0, data: null });

  // ===== Origem dos leads (donut) =====
  const origemMap = { forms: 'Site MF', 'teste-agente': 'Teste interno' };
  const origemTally = {};
  for (const l of leads) {
    const raw = l.origem || '(não informada)';
    const label = origemMap[raw] || raw;
    origemTally[label] = (origemTally[label] || 0) + 1;
  }
  const origens = Object.entries(origemTally)
    .map(([label, n]) => ({ label, n }))
    .sort((a, b) => b.n - a.n);

  // ===== Funil 6 etapas =====
  const conta = (pred) => leads.filter(pred).length;
  const novos = conta(l => l.status === 'novo');
  const emAtendimento = conta(l => l.status === 'atendimento humano' || l.status === 'fallback_whatsapp');
  const qualificados = conta(l => l.qualificacao === 'qualificado' || l.qualificacao === 'sim');
  const agendados = conta(l => l.status === 'agendado' || l.data_e_hora_agendamento);
  const compareceu = conta(l => l.status === 'comparecimento' || l.lista_status === 'comparecimento');
  const negociacao = conta(l => l.status === 'negociacao' || l.lista_status === 'negociacao');
  const vendidos = conta(l => l.status === 'venda' || l.valor_venda > 0);

  const total = leads.length;
  const funilTotal = Math.max(novos + emAtendimento + qualificados + agendados, 1);
  const funilBase = Math.max(novos, 1);
  const funil = [
    { etapa: 'Novos', n: novos, pct: 100 },
    { etapa: 'Em atendimento', n: emAtendimento, pct: Math.round((emAtendimento / funilBase) * 100) },
    { etapa: 'Qualificados', n: qualificados, pct: Math.round((qualificados / funilBase) * 100) },
    { etapa: 'Agendados', n: agendados, pct: Math.round((agendados / funilBase) * 100) },
    { etapa: 'Compareceu', n: compareceu, pct: Math.round((compareceu / funilBase) * 100) },
    { etapa: 'Negociação', n: negociacao, pct: Math.round((negociacao / funilBase) * 100) },
    { etapa: 'Vendidos', n: vendidos, pct: Math.round((vendidos / funilBase) * 100) },
  ];
  const conversaoLeadVenda = funilBase > 0 ? ((vendidos / funilBase) * 100).toFixed(1) : '0';

  // ===== Leads agregados por product_id (Bianca) =====
  const prodMap = Object.fromEntries(produtos.map(p => [p.product_id, p.Nome]));
  const leadsPorProduto = {};
  const ultimoPorProduto = {};
  for (const l of leads) {
    if (!l.product_id) continue;
    leadsPorProduto[l.product_id] = (leadsPorProduto[l.product_id] || 0) + 1;
    const ts = l.created_at ? new Date(l.created_at).getTime() : 0;
    if (!ultimoPorProduto[l.product_id] || ts > ultimoPorProduto[l.product_id]) {
      ultimoPorProduto[l.product_id] = ts;
    }
  }

  // ===== Imóveis em destaque (mf_imoveis publicados, leads via vínculo bianca_product_id) =====
  const imoveisDestaque = (imoveis || [])
    .filter(i => i.status === 'publicado')
    .map(i => ({
      ...i,
      leadsCount: i.bianca_product_id ? (leadsPorProduto[i.bianca_product_id] || 0) : null,
      campanhaNome: i.bianca_product_id ? prodMap[i.bianca_product_id] : null,
      ultimoLead: i.bianca_product_id ? ultimoPorProduto[i.bianca_product_id] : null,
    }))
    .sort((a, b) => {
      const aLeads = a.leadsCount || 0;
      const bLeads = b.leadsCount || 0;
      if (bLeads !== aLeads) return bLeads - aLeads;
      if (Boolean(b.destaque) !== Boolean(a.destaque)) return b.destaque ? 1 : -1;
      return (b.preco || 0) - (a.preco || 0);
    })
    .slice(0, 5);

  // ===== Leads quentes (score-based) =====
  // Combina recência de interação, status do funil e agendamento futuro pra
  // destacar leads que merecem contato agora.
  const THREE_D = 3 * 24 * 3600 * 1000;
  const SEVEN_D = 7 * 24 * 3600 * 1000;
  const leadsQuentesAll = leads
    .map(l => {
      const status = String(l.status || '').toLowerCase();
      if (status === 'venda' || status === 'perdido' || status === 'descartado') return null;
      let score = 0;
      const reasons = [];
      const ag = parseAgendamento(l.data_e_hora_agendamento);
      if (ag && ag >= today) { score += 4; reasons.push('visita marcada'); }
      if (l.qualificacao === 'qualificado' || l.qualificacao === 'sim') { score += 3; reasons.push('qualificado'); }
      const ultMsg = l.ultima_mensagem_recebida ? new Date(l.ultima_mensagem_recebida).getTime() : 0;
      if (ultMsg && now - ultMsg < THREE_D) { score += 3; reasons.push('msg recente'); }
      else if (ultMsg && now - ultMsg < SEVEN_D) { score += 2; reasons.push('msg essa semana'); }
      if (status.includes('atendimento humano')) { score += 2; reasons.push('em atendimento'); }
      if ((l.qtd_fup || 0) >= 2) { score += 1; reasons.push(`${l.qtd_fup} FUPs`); }
      const created = l.created_at ? new Date(l.created_at).getTime() : 0;
      if (created && now - created < SEVEN_D) { score += 1; reasons.push('novo'); }
      if (score < 3) return null;
      return {
        lead_id: l.lead_id,
        nome: l.nome,
        telefone: l.telefone,
        score,
        reasons,
        produto: l.produto || prodMap[l.product_id] || null,
      };
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score);
  const leadsQuentes = leadsQuentesAll.slice(0, 6);

  // ===== Imóveis encalhados =====
  // Publicados há >=60d, sem nenhum lead vinculado via bianca_product_id.
  // Ordena pelos mais antigos primeiro — sinal de catálogo a revisar.
  const imoveisEncalhadosAll = (imoveis || [])
    .filter(i => i.status === 'publicado')
    .map(i => {
      const created = i.created_at ? new Date(i.created_at).getTime() : null;
      const idadeDias = created ? Math.floor((now - created) / (24 * 3600 * 1000)) : null;
      const leadsCount = i.bianca_product_id ? (leadsPorProduto[i.bianca_product_id] || 0) : 0;
      return { ...i, idadeDias, leadsCount };
    })
    .filter(i => i.idadeDias !== null && i.idadeDias >= 60 && i.leadsCount === 0)
    .sort((a, b) => b.idadeDias - a.idadeDias);
  const imoveisEncalhados = imoveisEncalhadosAll.slice(0, 6);

  return {
    kpis: {
      leadsNoMes, leadsMesAnterior, trendPct,
      conversaoMes, conversaoTrend,
      visitasProx7, visitasHoje, visitasAmanha,
      ticketMedio,
      leadsQuentesTotal: leadsQuentesAll.length,
      imoveisEncalhadosTotal: imoveisEncalhadosAll.length,
    },
    serieDias, totalPeriodo, mediaDia, pico,
    origens,
    funil, conversaoLeadVenda,
    imoveisDestaque,
    visitas: visitasOrdenadas.slice(0, 6),
    leadsQuentes,
    imoveisEncalhados,
    totalLeads: total,
    totalImoveis: imoveis.length,
    imoveisPublicados: imoveis.filter(i => i.status === 'publicado').length,
  };
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function parseAgendamento(s) {
  if (!s) return null;
  const str = String(s).trim();
  const iso = new Date(str);
  if (!isNaN(iso.getTime())) return iso;
  const m = str.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})/);
  if (m) {
    const [, d, mo, y, h, mi] = m;
    return new Date(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi));
  }
  return null;
}

export function formatBRL(v, full = false) {
  if (!v) return '—';
  if (full) return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });
  if (v >= 1_000_000) return `R$ ${(v / 1_000_000).toFixed(1).replace('.', ',')} mi`;
  if (v >= 1_000) return `R$ ${(v / 1_000).toFixed(0)}k`;
  return `R$ ${v.toFixed(0)}`;
}
